/**
 * AdminNodeHealthPanel — FEATURE A: passive per-node health.
 *
 * Reads GET /admin/health/nodes, which scores each result_type over a rolling
 * window of its last N real executions. No synthetic runs, no credit cost.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Activity, RefreshCw, Loader2, CheckCircle2, AlertTriangle,
  XCircle, MinusCircle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';

const STATUS_META = {
  ok: { label: 'OK', icon: CheckCircle2, cls: 'ok' },
  degraded: { label: 'Degradado', icon: AlertTriangle, cls: 'degraded' },
  error: { label: 'Error', icon: XCircle, cls: 'error' },
  no_data: { label: 'Sin datos recientes', icon: MinusCircle, cls: 'nodata' },
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

export default function AdminNodeHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getNodesHealth();
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar la salud de los nodos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="admin-health-loading" data-testid="node-health-loading">
        <Loader2 size={18} className="animate-spin" /> Calculando salud de nodos…
      </div>
    );
  }

  const nodes = data?.nodes || [];
  const summary = data?.summary || {};

  return (
    <div className="admin-health-panel" data-testid="node-health-panel">
      <div className="admin-health-head">
        <div className="admin-health-title">
          <Activity size={15} />
          <span>Salud por nodo</span>
          <em>
            ventana móvil: últimas {data?.window?.size} ejecuciones · OK ≥ {data?.window?.ok_min}% ·
            Degradado ≥ {data?.window?.degraded_min}%
          </em>
        </div>
        <button className="admin-refresh" onClick={load} data-testid="node-health-refresh">
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      <div className="admin-health-summary" data-testid="node-health-summary">
        {['ok', 'degraded', 'error', 'no_data'].map((k) => {
          const M = STATUS_META[k];
          return (
            <div key={k} className={`admin-health-chip ${M.cls}`} data-testid={`node-health-count-${k}`}>
              <M.icon size={13} />
              <strong>{summary[k] ?? 0}</strong>
              <span>{M.label}</span>
            </div>
          );
        })}
      </div>

      <div className="admin-health-grid" data-testid="node-health-grid">
        {nodes.map((n) => {
          const M = STATUS_META[n.status] || STATUS_META.no_data;
          const open = expanded === n.result_type;
          return (
            <div
              key={n.result_type}
              className={`admin-health-card ${M.cls}`}
              data-testid={`node-health-card-${n.result_type}`}
            >
              <div className="admin-health-card-top">
                <span className="admin-health-node-name">{n.label}</span>
                <span className={`admin-health-badge ${M.cls}`} data-testid={`node-health-badge-${n.result_type}`}>
                  <M.icon size={11} /> {M.label}
                </span>
              </div>

              <div className="admin-health-metrics">
                <span className="admin-health-rate" data-testid={`node-health-rate-${n.result_type}`}>
                  {n.success_rate === null ? '—' : `${n.success_rate}%`}
                </span>
                <span className="admin-health-counts">
                  {n.completed} ok · {n.failed} fallos · {n.window_count} en ventana
                </span>
              </div>

              <div className="admin-health-bar">
                <div
                  className={`admin-health-bar-fill ${M.cls}`}
                  style={{ width: `${n.success_rate ?? 0}%` }}
                />
              </div>

              <div className="admin-health-foot">
                <span>Último éxito: {fmtWhen(n.last_success_at)}</span>
                {n.last_error && (
                  <button
                    className="admin-health-error-toggle"
                    onClick={() => setExpanded(open ? null : n.result_type)}
                    data-testid={`node-health-error-toggle-${n.result_type}`}
                  >
                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Último error
                  </button>
                )}
              </div>

              {open && n.last_error && (
                <div className="admin-health-error" data-testid={`node-health-error-${n.result_type}`}>
                  <p className="admin-health-error-msg">{n.last_error.message}</p>
                  <p className="admin-health-error-when">{fmtWhen(n.last_error.at)}</p>
                  <pre className="admin-health-error-params">
                    {JSON.stringify(n.last_error.params, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
